import { useMemo } from 'react';
import { useAuth as useOidcAuth } from 'react-oidc-context';
import AppContainerFromCreditControl from '../../../credit-control/src/components/AppContainer';
import { useUserProfile } from '../../../credit-control/src/hooks/useUserProfile';
import { useAuth } from '../../../credit-control/src/providers/AuthProvider';
import { WootricProvider } from '../../../credit-control/src/providers/WootricProvider';
import type { IntercomUser } from '../../../credit-control/src/types/intercom';
import { useCookieConsentContext } from '../providers/CookieConsentProvider';
import { useUserContext } from '../providers/UserContextProvider';

interface CreditControlWithUserContextProps {
  apiUrl: string;
  wootricToken?: string;
  environment?: string;
  feedbackAccountToken?: string;
  feedbackApiHost?: string;
  feedbackMfeVersion?: string;
  feedbackReDisplayAfterDays?: string;
  canUseDemoData?: boolean;
  canForceSync?: boolean;
  intercomAppId?: string;
  documentApiUrl?: string;
  paysuiteOrchestratorName?: string;
  configCatSdkKey?: string;
}

/**
 * Wrapper component that bridges UserContextProvider with the credit-control AppContainer.
 * Passes the userContext companies to CompanyProvider via the settings prop.
 * Also integrates WootricProvider for NPS survey functionality.
 */
export function CreditControlWithUserContext({
  apiUrl,
  wootricToken,
  environment,
  feedbackAccountToken,
  feedbackApiHost,
  feedbackMfeVersion,
  feedbackReDisplayAfterDays,
  canUseDemoData,
  canForceSync,
  intercomAppId,
  documentApiUrl,
  paysuiteOrchestratorName,
  configCatSdkKey,
}: CreditControlWithUserContextProps): React.ReactElement {
  const { companies, isLoading, userContext, refetch } = useUserContext();
  const { cookieConsent } = useCookieConsentContext();
  const { user: wootricUser } = useUserProfile({ userContext, isLoading });
  const { instanceId } = useAuth();
  const oidcAuth = useOidcAuth();

  const email = userContext?.email ?? oidcAuth.user?.profile?.email;
  const intercomName =
    userContext?.firstName && userContext?.lastName ? `${userContext.firstName} ${userContext.lastName}` : undefined;
  const intercomUser: IntercomUser | null = useMemo(
    () => (email && instanceId ? { email, instanceId, name: intercomName } : null),
    [email, instanceId, intercomName],
  );

  return (
    <WootricProvider
      user={wootricUser}
      wootricToken={wootricToken}
      environment={environment}
      cookieConsent={cookieConsent}
    >
      <AppContainerFromCreditControl
        settings={{
          apiUrl,
          host: 'app-prod',
          externalCompanies: companies,
          externalCompaniesLoading: isLoading,
          onRefreshUserContext: refetch,
          feedbackAccountToken,
          feedbackApiHost,
          feedbackMfeVersion,
          feedbackReDisplayAfterDays,
          canUseDemoData,
          canForceSync,
          intercomUser,
          intercomAppId,
          cookieConsent,
          documentApiUrl,
          paysuiteOrchestratorName,
          configCatSdkKey,
        }}
      />
    </WootricProvider>
  );
}
